import React, { useEffect, useState } from "react";
import Resizer from "react-image-file-resizer";
import { organizationRepo } from "../../services";
import { LOGO_MAX_CHARS, LOGO_MAX_PX, NAME_MAX, brandingOf, sanitizeBranding, validColor } from "../../config/branding";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Card, Input, PageHead, UploadBox } from "../../components/ui";
import BrandMark from "../../components/BrandMark";

// ย่อรูปเป็น PNG ไม่เกิน 256px — ถ้า data URL ใหญ่เกินเซลล์ชีตให้ลดขนาดลงทีละขั้น
const resizeLogo = (file) =>
  new Promise((resolve, reject) => {
    const sizes = [LOGO_MAX_PX, 192, 128, 96];
    const attempt = (index) => {
      if (index >= sizes.length) return reject(new Error("รูปโลโก้ซับซ้อนเกินไป กรุณาใช้รูปที่เรียบง่ายกว่านี้"));
      try {
        Resizer.imageFileResizer(file, sizes[index], sizes[index], "PNG", 100, 0, (uri) => {
          if (typeof uri === "string" && uri.length <= LOGO_MAX_CHARS) resolve(uri);
          else attempt(index + 1);
        }, "base64");
      } catch (err) {
        reject(err);
      }
    };
    attempt(0);
  });

export default function Branding() {
  const { organization, refreshOrganizations, toast } = useApp();
  const { tx } = useI18n();
  const [form, setForm] = useState({ displayName: "", logo: "", color: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ displayName: "", logo: "", color: "", ...sanitizeBranding(organization?.branding) });
  }, [organization]);

  const onFile = async (file) => {
    if (!file.type.startsWith("image/")) return toast(tx("เลือกไฟล์รูปภาพเท่านั้น", "Please choose an image file"), "error");
    try {
      const logo = await resizeLogo(file);
      setForm((prev) => ({ ...prev, logo }));
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const save = async (next) => {
    if (!organization) return;
    if (next.color && !validColor(next.color)) return toast(tx("สีต้องเป็นรหัส #rrggbb", "Color must be #rrggbb"), "error");
    setSaving(true);
    try {
      // ส่งเฉพาะ branding — ไม่ส่งทั้งแถวร้านกลับไป กันทับแพ็คเสริม/แพ็กเกจที่เพิ่งเปลี่ยนจากหน้าอื่น
      await organizationRepo.update(null, organization.id, { branding: sanitizeBranding(next) });
      await refreshOrganizations();
      toast(tx("บันทึกแบรนด์เรียบร้อย", "Branding saved"), "success");
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // ตัวอย่างใช้ brandingOf ผ่านร้านจำลองที่มีสิทธิ์ เพื่อให้เห็นผลก่อนบันทึก
  const preview = brandingOf({ ...organization, branding: form, plan: "enterprise_plus", planExpireAt: new Date(Date.now() + 86400000).toISOString() });

  if (!organization) return null;

  return (
    <>
      <PageHead
        title={tx("แบรนด์ร้านเอง (White-label)", "White-label branding")}
        subtitle={tx("ลูกค้าของคุณจะเห็นชื่อ โลโก้ และสีของร้านแทน LucidCRM ในหน้าลูกค้า หน้าสมัครผ่านลิงก์ร้าน และเอกสาร", "Your customers see your name, logo and color instead of LucidCRM on the customer app, shop sign-up page and documents")}
        actions={<Button variant="primary" disabled={saving} onClick={() => save(form)}>{tx("บันทึก", "Save")}</Button>}
      />

      <div className="grid-2" style={{ alignItems: "start" }}>
        <Card>
          <Input
            label={tx("ชื่อที่แสดง", "Display name")}
            maxLength={NAME_MAX}
            value={form.displayName}
            onChange={(event) => setForm({ ...form, displayName: event.target.value })}
            hint={tx(`เว้นว่าง = ใช้ชื่อธุรกิจ (${organization.name})`, `Empty = business name (${organization.name})`)}
          />
          <div className="field-crm">
            <label>{tx(`โลโก้ (ย่อให้อัตโนมัติ ไม่เกิน ${LOGO_MAX_PX}px)`, `Logo (auto-resized, max ${LOGO_MAX_PX}px)`)}</label>
            <UploadBox onFile={onFile} preview={form.logo} hint={tx("คลิกเพื่อเลือกรูปโลโก้", "Click to choose a logo")} />
            {form.logo ? (
              <Button className="u-mt-8" size="sm" onClick={() => setForm({ ...form, logo: "" })}>{tx("ลบโลโก้", "Remove logo")}</Button>
            ) : null}
          </div>
          <div className="field-crm">
            <label>{tx("สีหลัก", "Primary color")}</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="color" value={validColor(form.color) ? form.color : "#4f46e5"} onChange={(event) => setForm({ ...form, color: event.target.value })} aria-label={tx("เลือกสี", "Pick a color")} />
              <input className="input-crm" style={{ maxWidth: 140 }} value={form.color} placeholder="#4f46e5" onChange={(event) => setForm({ ...form, color: event.target.value })} />
              {form.color ? <Button size="sm" onClick={() => setForm({ ...form, color: "" })}>{tx("ใช้สีปกติ", "Default")}</Button> : null}
            </div>
          </div>
          <Button disabled={saving} onClick={() => save({ displayName: "", logo: "", color: "" })}>
            {tx("ล้างแบรนด์ (กลับไปใช้ LucidCRM)", "Reset to LucidCRM branding")}
          </Button>
        </Card>

        <Card title={tx("ตัวอย่าง", "Preview")}>
          <div className="card-crm" style={{ ...preview.cssVars, display: "flex", alignItems: "center", gap: 12 }}>
            <BrandMark brand={preview} size={44} />
            <div>
              <div className="u-strong" style={{ fontSize: 18 }}>{preview.name}</div>
              <div className="u-fs-12 u-muted">{tx("แถบด้านข้างและหน้าสมัครลูกค้า", "Sidebar and customer sign-up page")}</div>
            </div>
          </div>
          <div className="u-fs-12 u-muted u-mt-8">
            {tx("หากแพ็กเกจหมดอายุหรือหมดสิทธิ์ White-label ระบบจะกลับไปใช้แบรนด์ LucidCRM อัตโนมัติ (ข้อมูลที่ตั้งไว้ยังอยู่)", "If your plan or White-label access expires, LucidCRM branding returns automatically (your settings are kept)")}
          </div>
        </Card>
      </div>
    </>
  );
}
